import React from 'react';
import PropTypes from 'prop-types';
import Avatar from '../Avatar';
import styles from './AvatarStack.module.scss';

const AvatarStack = ({ avatars, max, size, layout, className, ...rest }) => {
  if (layout === 'grid') {
    const count = avatars.length;

    // A single avatar collapses to a plain Avatar — no point drawing a
    // 1-cell grid, and it keeps single-DP rows visually identical to a
    // standalone Avatar elsewhere in the UI.
    if (count <= 1) {
      return (
        <Avatar
          src={avatars[0]?.src}
          name={avatars[0]?.name}
          size={size}
          className={className}
          {...rest}
        />
      );
    }

    // Cap visible cells at 4. If there are more, the last cell becomes a
    // "+N" bubble so the count of additional members is still surfaced.
    const variant = count === 2 ? 2 : count === 3 ? 3 : 4;
    const hasOverflow = count > 4;
    const visibleCount = hasOverflow ? 3 : Math.min(count, 4);
    const visible = avatars.slice(0, visibleCount);
    const overflow = count - visibleCount;

    const classNames = [
      styles.grid,
      styles[size],
      styles[`grid--${variant}`],
      className || '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={classNames} {...rest}>
        {visible.map((avatar, index) => (
          <div className={styles.cell} key={index}>
            <Avatar src={avatar.src} name={avatar.name} size={size} />
          </div>
        ))}
        {hasOverflow && (
          <div className={`${styles.cell} ${styles.cellOverflow}`}>
            +{overflow}
          </div>
        )}
      </div>
    );
  }

  const visible = avatars.slice(0, max);
  const overflow = avatars.length - max;

  const classNames = [styles.stack, className || ''].filter(Boolean).join(' ');

  return (
    <div className={classNames} {...rest}>
      {visible.map((avatar, index) => (
        <div
          className={`${styles.item} ${index === 0 ? '' : styles.overlap}`}
          key={index}
        >
          <Avatar
            src={avatar.src}
            name={avatar.name}
            size={size}
          />
        </div>
      ))}
      {overflow > 0 && (
        <div className={`${styles.item} ${styles.overlap}`}>
          <div className={`${styles.overflow} ${styles[size]}`}>
            <span>+{overflow}</span>
          </div>
        </div>
      )}
    </div>
  );
};

AvatarStack.propTypes = {
  avatars: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  max: PropTypes.number,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  layout: PropTypes.oneOf(['stack', 'grid']),
  className: PropTypes.string,
};

AvatarStack.defaultProps = {
  max: 3,
  size: 'sm',
  layout: 'stack',
  className: '',
};

export default AvatarStack;
